import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.presentation === 'grid') {
      this.#setupGridProgress();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #setupGridProgress() {
    const gallery = /** @type {HTMLElement | null} */ (this.querySelector('.media-gallery__grid-container'));
    const track = /** @type {HTMLElement | null} */ (this.querySelector('.media-gallery__scrollbar-track'));
    const thumb = /** @type {HTMLElement | null} */ (this.querySelector('.media-gallery__scrollbar-thumb'));
    const up = /** @type {HTMLButtonElement | null} */ (this.querySelector('.media-gallery__scrollbar-button--up'));
    const down = /** @type {HTMLButtonElement | null} */ (this.querySelector('.media-gallery__scrollbar-button--down'));

    if (!gallery || !track || !thumb || !up || !down) return;

    const update = () => {
      const rect = gallery.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const total = rect.height + viewportHeight;
      const progress = Math.min(Math.max((viewportHeight - rect.top) / total, 0), 1);

      const trackHeight = track.clientHeight;
      const thumbHeight = Math.max(trackHeight * 0.15, 16);
      const maxOffset = trackHeight - thumbHeight;

      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${maxOffset * progress}px)`;
    };

    const scrollHandler = () => update();
    const resizeHandler = () => update();

    window.addEventListener('scroll', scrollHandler, { signal: this.#controller.signal });
    window.addEventListener('resize', resizeHandler, { signal: this.#controller.signal });

    up.addEventListener('click', () => {
      window.scrollBy({ top: -window.innerHeight * 0.5, behavior: 'smooth' });
    });

    down.addEventListener('click', () => {
      window.scrollBy({ top: window.innerHeight * 0.5, behavior: 'smooth' });
    });

    queueMicrotask(update);
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
